<template>
  <div>
    <div class="p-box">
      <header class="p-box__header">
        <ul class="c-links">
          <li
            v-for="menu in menus"
            :key="menu"
            class="c-links__item"
          >
            <strong
              :class="selectedMenu === menu && 'is-current'"
              @click="clickMenu(menu)"
            >
              {{ $t('pages.CompaniesLocationStore.tabs.' + menu ) }}
            </strong>
          </li>
        </ul>
      </header>
      <div v-show="selectedMenu === 'information'" class="p-box__body">
        <KkInfo
          v-if="gmbOpenInfoStatus && ['closedTemporarily', 'closedPermanently'].includes(gmbOpenInfoStatus)"
          icon="exclamation"
          class="openInfoStatusCaution"
          alert
        >
          <p class="openInfoStatusCaution__text">
            {{ $t('pages.CompaniesLocationStore.openInfoStatusCaution', { openInfoStatus: displayStoreGmbOpenStatus }) }}
            {{ $t('pages.CompaniesLocationStore.openInfoStatusEdit') }}<router-link :to="{ name: 'CompaniesLocationStoreOpenInfoEdit', params: { company_uuid: companyUuid, store_uuid: storeUuid } }">
              {{ $t('pages.CompaniesLocationStore.openInfoStatusEditLinkLabel') }}
            </router-link>
          </p>
        </KkInfo>
        <div class="c-grid">
          <div class="c-grid__items">
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__12 c-grid__item__l__12">
              <section class="p-box">
                <header class="p-box__header">
                  <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStore.basicInformation') }}</h3>
                </header>
                <div class="p-box__body">
                  <Loading v-show="store_loading || sites_loading" />
                  <template v-if="store && !store_loading && !sites_loading">
                    <div class="p-storeInfo">
                      <div class="p-storeInfo__items">
                        <div class="p-storeInfo__brand">
                          <div v-if="!isDocomoPlan" class="p-storeInfo__brand__icon">
                            <img :src="store.attributes.brand_image_url" alt="">
                          </div>
                          <div class="p-storeInfo__brand__name">{{ store.attributes.brand_name }}</div>
                        </div>
                        <div class="p-storeInfo__content storeInfo-content-extended">
                          <h1 class="p-storeInfo__name">
                            {{ store.attributes.name }}
                            <template v-if="store_gmb_connected">
                              <Icon v-show="gmbLocationSynced.name_synced" name="iconProtection" />
                            </template>
                          </h1>
                          <div v-if="store.attributes.reviews_count" class="p-storeInfo__reviews">
                            <div class="c-rating">
                              <div class="c-rating__number">
                                {{ formatAverage(store.attributes.reviews_average_rating) }}
                              </div>
                              <Star :rating="formatAverage(store.attributes.reviews_average_rating)" />
                            </div>
                            <p class="p-storeInfo__reviews__count">
                              {{ store.attributes.reviews_count }} {{ $t('models.store.reviewsCount') }}
                            </p>
                          </div>
                          <div class="siteLists">
                            <KkSiteList type="connected" :site-types="sitesDisplayedOnBasicInfo.connectedSites" class="siteLists__item" />
                            <KkSiteList type="unconnected"
                                        :site-types="sitesDisplayedOnBasicInfo.unconnectedSites"
                                        @move-to-connection-tab="clickMenu('connection')"
                            />
                          </div>
                          <ul class="p-storeInfo__detail">
                            <li>
                              <div class="p-storeInfo__detail__icon">
                                <img :src="iconMapMarkerhPath" alt="" class="p-stores__action__icon">
                              </div>
                              <span>
                                {{ store.attributes.postal_code }} {{ store.attributes.full_address }}
                                <template v-if="store_gmb_connected">
                                  <Icon v-show="gmbLocationSynced.address_synced" name="iconProtection" />
                                </template>
                              </span>
                            </li>
                            <li>
                              <div class="p-storeInfo__detail__icon">
                                <img :src="iconPhonePath" alt="" class="p-stores__action__icon">
                              </div>
                              <span>
                                {{ [store.attributes.primary_phone, ...store.attributes.additional_phones].map(field => field.number).join(' | ') }}
                                <template v-if="store_gmb_connected">
                                  <Icon v-show="gmbLocationSynced.phone_synced" name="iconProtection" />
                                </template>
                              </span>
                              <div v-if="!isDocomoPlan" class="p-storeInfo__detail__icon--right">
                                <div class="icon">
                                  <KkIcon name="iconFax" />
                                </div>
                              </div>
                              <span>
                                {{ store.attributes.fax_phone_number }}
                              </span>
                            </li>
                            <li>
                              <div class="p-storeInfo__detail__icon">
                                <img :src="iconWebsiteLinkPath" alt="" class="p-stores__action__icon">
                              </div>
                              <span class="webSiteUrl">
                                {{ webSiteUrl }}
                                <template v-if="store_gmb_connected">
                                  <Icon v-show="gmbLocationSynced.website_url_synced" name="iconProtection" />
                                </template>
                              </span>
                              <span
                                v-if="showWebSiteLink"
                                class="previewWebsiteLink"
                                @click="showWebsiteLinkPreview=true"
                              >
                                {{ $t('pages.CompaniesLocationStore.websiteLink.showPreview') }}
                              </span>
                            </li>
                          </ul>
                          <div v-if="store.attributes.business_type_name !== '宿泊' && !isDocomoPlan" class="p-storeInfo-description">
                            <p class="p-storeInfo-description__label">
                              {{ $t('models.store.description') }}:
                              <template v-if="store_gmb_connected">
                                <Icon v-show="gmbLocationSynced.description_synced" name="iconProtection" />
                              </template>
                            </p>
                            <span class="p-storeInfo-description__content">
                              {{ store.attributes.description }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority && !isDocomoPlan" class="p-storeInfo-footer">
                        <div class="c-button c-button--right">
                          <router-link :to="{ name: 'CompaniesLocationStoreEdit', params: { company_uuid: companyUuid, store_uuid: storeUuid } }" class="c-button__item c-button__item--primary">
                            {{ $t('labels.change') }}
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <!-- 写真ライブラリ -->
      <div v-show="selectedMenu === 'information'" class="p-box__body">
        <MediaLibraries
          v-if="providedFeaturesMediaLibrary"
          :media-libraries="mediaLibraries"
          :total-count="mediaLibrariesMeta.totalCount"
          :loading="mediaLibrariesLoading"
          :is-enabled="store_gmb_connected || store?.attributes.connected_yahoo_place"
          :is-editable="showMoreThanOperatorAuthority"
        />
      </div>
      <!-- 営業時間、決済手段 -->
      <div v-show="selectedMenu === 'information'" class="p-box__body">
        <div class="c-grid">
          <div class="c-grid__items">
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__6 c-grid__item__l__6">
              <section class="p-box">
                <header class="p-box__header">
                  <h3 class="p-box__title">
                    {{ $t('models.storeRegularHour.modelName') }}
                    <template v-if="store_gmb_connected">
                      <Icon v-show="gmbLocationSynced.business_hours_synced" name="iconProtection" />
                    </template>
                  </h3>
                </header>
                <div class="p-box__body">
                  <Loading v-if="storeRegularHoursLoading || storeBusinessHoursCrawlersLoading || storeExternalAccountsLoading" />
                  <div v-else>
                    <BusinessHoursCrawlerStatus v-if="providedFeaturesBusinessHoursCrawler" :status="storeBusinessHoursCrawlerStatus" />
                    <div v-if="isRegularHourSet" class="p-store-times">
                      <ul v-for="weekday in weeks" :key="weekday" class="p-store-times__items">
                        <li class="p-store-times__item">
                          <div class="p-store-times__title">
                            {{ displayWeekday(weekday) }}
                          </div>
                          <span class="p-store-times__content">
                            {{ displayHour('regularHour')(regularHour(weekday)) }}
                          </span>
                        </li>
                      </ul>
                    </div>
                    <div v-else>
                      {{ $t('models.storeSpecialHour.notSet') }}
                    </div>
                    <div v-if="storeHolidayRegularHour" class="p-store-times holidayIndexHour">
                      <ul v-if="storeHolidayRegularHour" class="p-store-times__items">
                        <li class="p-store-times__item">
                          <div class="p-store-times__title">
                            {{ $t('labels.holiday') }}
                          </div>
                          <span class="p-store-times__content">
                            {{ displayHour('regularHour')(storeHolidayRegularHour) }}
                          </span>
                        </li>
                      </ul>
                      <ul v-if="storeDayBeforeHolidayRegularHour" class="p-store-times__items">
                        <li class="p-store-times__item">
                          <div class="p-store-times__title">
                            {{ $t('labels.dayBeforeHoliday') }}
                          </div>
                          <span class="p-store-times__content">
                            {{ displayHour('regularHour')(storeDayBeforeHolidayRegularHour) }}
                          </span>
                        </li>
                      </ul>
                      <div v-if="storeWdayIndexRegularHours.length" class="p-store-times__items">
                        <ul v-for="wdayIndexRegularHour in storeWdayIndexRegularHours" :key="wdayIndexRegularHour">
                          <div v-for="index in wdayIndexRegularHour.indexes" :key="index">
                            <div v-for="wday in wdayIndexRegularHour.wdays" :key="wday">
                              <li class="p-store-times__item">
                                <div class="p-store-times__title">
                                  {{ $t('labels.wdayIndex', {index: index, wday: $t('labels.weekday.' + wday )}) }}
                                </div>
                                <span class="p-store-times__content">
                                  {{ displayHour('regularHour')(wdayIndexRegularHour) }}
                                </span>
                              </li>
                            </div>
                          </div>
                        </ul>
                      </div>
                    </div>
                    <div v-if="storeBusinessHoursMeta.moreHoursTypeApiError" class="moreHour">
                      <div class="moreHour__title">
                        {{ $t('models.moreHour.modelName') }}
                      </div>
                      <p style="color: #E9446A;">
                        {{ $t('models.moreHour.moreHoursTypeApiError.info') }}
                      </p>
                    </div>
                    <div v-else-if="storeMoreHours.length !== 0">
                      <div v-for="storeMoreHour in storeMoreHours" :key="storeMoreHour.gmbHoursTypeId" class="moreHour">
                        <div class="moreHour__title">
                          {{ displayMoreHoursTypeName(storeMoreHour.gmbHoursTypeId) }}
                        </div>
                        <div class="p-store-times">
                          <ul v-for="weekMoreHour in storeMoreHour.weeks" :key="weekMoreHour" class="p-store-times__items">
                            <li class="p-store-times__item">
                              <div class="p-store-times__title">
                                {{ displayWeekday(weekMoreHour.wday) }}
                              </div>
                              <span class="p-store-times__content">
                                {{ displayHour('moreHour')(weekMoreHour) }}
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div v-if="!isDocomoPlan" class="specialHours">
                      <div class="specialHours__title">
                        {{ $t('pages.CompaniesLocationStore.businessHours.specialHours.title') }}
                      </div>
                      <div v-if="futureSpecialHours.length === 0">
                        {{ $t('pages.CompaniesLocationStore.businessHours.specialHours.notSet') }}
                      </div>
                      <div v-else class="p-store-times">
                        <ul v-for="hour in futureSpecialHours" :key="hour.date" class="p-store-times__items">
                          <li class="p-store-times__item">
                            <div class="p-store-times__title">
                              {{ hour.date }}
                            </div>
                            <span class="p-store-times__content">
                              {{ displayHour('specialHour')(hour) }}
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div v-if="pastSpecialHours.length">
                        <span
                          class="specialHours__modalLink"
                          @click="showPastSpecialHoursModal = true"
                        >
                          {{ $t('pages.CompaniesLocationStore.businessHours.specialHours.showPast') }}
                        </span>
                      </div>
                    </div>
                    <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority" class="c-button c-button--right">
                      <span
                        v-if="!isDocomoPlan"
                        class="previewBusinessHourLink"
                        @click="showBusinessHourPreview=true"
                      >
                        {{ $t('pages.CompaniesLocationStore.businessHours.showPreview') }}
                      </span>
                      <router-link :to="{ name: 'CompaniesLocationStoreRegularHoursEdit', params: { company_uuid: companyUuid, store_uuid: storeUuid } }" class="c-button__item c-button__item--primary">
                        {{ $t('labels.change') }}
                      </router-link>
                    </div>
                  </div>
                </div>
              </section>
            </div>
            <KkPaymentMethodList
              v-if="providedFeaturesPaymentMethod"
              :payment-method-synced="gmbLocationSynced.payment_method_synced"
              :loading="store_gmb_loading || payment_methods_loading || storeExternalAccountsLoading"
              :preview-loading="paymentMethodsPreviewLoading"
              :payment-method="payment_methods"
              :payment-method-preview="paymentMethodsPreview"
              :can-change="canDisplayChangeUi && showMoreThanEditorAuthority"
              :linked-sites="linkedSites"
              @show-preview="handleShowPaymentMethodsPreview"
            />
          </div>
        </div>
      </div>
      <KkPreviewModal
        v-if="showBusinessHourPreview"
        :is-visible="showBusinessHourPreview"
        :business-hour-synced="gmbLocationSynced.business_hours_synced"
        :title="$t('models.businessHours.previewTitle')"
        :show-submit="false"
        :linked-sites="linkedSites"
        :business-hour-request-body="businessHourList()"
        :enabled-business-hours-crawler="enabledBusinessHoursCrawler"
        @close="showBusinessHourPreview=false"
      />
      <KkWebsiteLinkPreviewModal
        v-if="showWebsiteLinkPreview"
        :is-visible="showWebsiteLinkPreview"
        :title="$t('pages.CompaniesLocationStore.websiteLink.previewTitle')"
        :items="store.attributes.site_types"
        @close="showWebsiteLinkPreview=false"
      />
      <!-- 連携サイトごとの設定 -->
      <div v-show="selectedMenu === 'information'" class="p-box__body">
        <section v-if="providedFeaturesGmb" class="p-box">
          <header class="p-box__header">
            <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStore.siteSetting.title') }}</h3>
          </header>
          <div class="p-box__body siteSettingSection">
            <Loading v-if="siteSettingLoading" />
            <template v-else>
              <div class="c-info">
                <div class="c-info__icon">
                  <Icon name="iconQuestion" />
                </div>
                <div class="c-info__content">
                  <p>{{ $t('pages.CompaniesLocationStore.siteSetting.info') }}</p>
                </div>
              </div>
              <div class="siteSettingSection__select">
                <Select
                  v-model="displaySiteSetting"
                  :options="siteSettingsSelects"
                />
              </div>
              <template v-if="displaySiteSetting === 'google'">
                <div v-if="store_gmb_connected" class="settings">
                  <div v-if="canDisplayChangeUi" class="settings__item">
                    <div class="settings__info">
                      <p class="settings__name">{{ $t('pages.CompaniesLocationStore.gmbProtection.title') }}</p>
                      <div class="settings__content">
                        <template v-if="!gmbProtectionLoading">
                          <template v-if="store_gmb_connected">
                            <div class="p-box__info">
                              <div class="c-info">
                                <div class="c-info__icon">
                                  <Icon name="iconProtection" />
                                </div>
                                <div class="c-info__content">
                                  <p>{{ $t('models.gmb.protection.description1') }}</p>
                                  <p>{{ $t('models.gmb.protection.description2') }}</p>
                                </div>
                              </div>
                            </div>
                            <fieldset class="p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <ul class="c-checkbox">
                                <li class="c-checkbox__item">
                                  <input
                                    id="update_store_gmb_synced"
                                    :disabled="!showMoreThanEditorAuthority"
                                    :checked="syncedAll"
                                    type="checkbox"
                                    class="c-checkbox__input"
                                    @change="handleChangeSyncedAll"
                                  >
                                  <label class="c-checkbox__label" for="update_store_gmb_synced">{{ $t('pages.CompaniesLocationStore.gmbProtection.protectAll') }}</label>
                                </li>
                              </ul>
                            </fieldset>
                          </template>
                          <template v-else>
                            {{ $t('messages.setupGmb') }}
                          </template>
                        </template>
                        <template v-else>
                          <Loading />
                        </template>
                      </div>
                    </div>
                    <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority" class="c-button">
                      <button class="c-button__item c-button__item--primary" @click="handleUpdateGmbLocationSubmit()">
                        {{ $t('labels.save') }}
                      </button>
                    </div>
                  </div>
                  <div class="settings__item">
                    <div class="settings__info">
                      <p class="settings__name">{{ $t('pages.CompaniesLocationStore.siteSetting.form.status') }}</p>
                      <p class="settings__content">
                        <span v-if="gmbOpenInfoStatus && gmbOpenInfoStatus !== 'openForBusinessUnspecified'"
                              :class="`statusMark__${gmbOpenInfoStatus}`"
                              class="statusMark"
                        />
                        {{ displayStoreGmbOpenStatus }}
                        <Icon v-if="store_gmb.attributes.open_info_synced" name="iconProtection" />
                      </p>
                    </div>
                    <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority" class="c-button">
                      <router-link
                        :to="{ name: 'CompaniesLocationStoreOpenInfoEdit', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
                        class="c-button__item c-button__item--primary"
                      >
                        {{ $t('labels.change') }}
                      </router-link>
                    </div>
                  </div>
                  <div class="settings__item">
                    <div class="settings__info">
                      <p class="settings__name">
                        {{ $t('pages.CompaniesLocationStore.siteSetting.form.category') }}
                        <Icon v-show="gmbLocationSynced.category_synced" name="iconProtection" />
                      </p>
                      <p class="settings__content">
                        {{ displayStoreGmbCategories }}
                      </p>
                      <Info v-if="!hotelBrand">
                        {{ $t('pages.CompaniesLocationStore.gmbCategory.info', { value: gmbUrlAttributesDisplayNames }) }}
                      </Info>
                    </div>
                    <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority" class="c-button">
                      <router-link
                        :to="{ name: 'CompaniesLocationStoreCategoriesEdit', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
                        class="c-button__item c-button__item--primary"
                      >
                        {{ $t('labels.change') }}
                      </router-link>
                    </div>
                  </div>
                  <template v-if="isCategorySet">
                    <div v-for="(url,urlIndex) in urlGroupDataFlattenList" :key="'url_' + url.displayName" class="settings__item">
                      <div class="settings__info">
                        <p class="settings__name">
                          {{ url.displayName }}：
                          <Icon v-show="gmbLocationSynced.category_synced" name="iconProtection" />
                        </p>
                        <div class="settings__content">
                          <div v-if="!url.uriValues">
                            {{ $t('messages.noSettings') }}
                          </div>
                          <p v-for="value in url.uriValues" :key="`${urlIndex}_${value.uri}`">
                            {{ value.uri }}
                          </p>
                          <Info v-if="url.parent === 'attributes/url_appointment' || url.parent === 'attributes/url_reservations'">
                            {{ $t('pages.CompaniesLocationStore.siteSetting.form.urlAppointmentInfo') }}
                          </Info>
                        </div>
                      </div>
                      <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority" class="c-button">
                        <router-link
                          :to="{ name: url.repeatable ? 'CompaniesLocationStoreMultipleUrlEdit' : 'CompaniesLocationStoreUrlEdit', params: { company_uuid: companyUuid, store_uuid: storeUuid, attribute_id: url.parent } }"
                          class="c-button__item c-button__item&#45;&#45;primary"
                        >
                          {{ $t('labels.change') }}
                        </router-link>
                      </div>
                    </div>
                    <div v-if="store.attributes.business_type_kind==='food' && !storeGmbFoodorderingsLoading" class="settings__item">
                      <div class="settings__info">
                        <p class="settings__name">
                          {{ $t('pages.CompaniesLocationStorePlaceActionLinksFoodorderingsEdit.basicTitle') }}：
                          <Icon v-show="gmbLocationSynced.category_synced" name="iconProtection" />
                        </p>
                        <div class="settings__content">
                          <div v-if="storeGmbFoodorderings.length === 0">
                            {{ $t('messages.noSettings') }}
                          </div>
                          <p v-for="storeGmbFoodordering in storeGmbFoodorderings" :key="storeGmbFoodordering.link">
                            {{ storeGmbFoodordering.link }}
                          </p>
                          <Info>
                            {{ $t('pages.CompaniesLocationStore.siteSetting.form.CannotBeSetUpThirdPartyProvidersLinks') }}
                          </Info>
                        </div>
                      </div>
                      <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority" class="c-button">
                        <router-link
                          :to="{ name: 'CompaniesLocationStorePlaceActionLinksFoodorderingsEdit', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
                          class="c-button__item c-button__item--primary"
                        >
                          {{ $t('labels.change') }}
                        </router-link>
                      </div>
                    </div>
                    <div v-if="hasSettableAttributes" class="settings__item settings__item&#45;&#45;attributes">
                      <div class="settings__info settings__info&#45;&#45;attributes">
                        <p class="settings__name">
                          {{ $t('pages.CompaniesLocationStore.siteSetting.form.attribute') }}
                          <Icon v-show="gmbLocationSynced.category_synced" name="iconProtection" />
                        </p>
                        <div class="settings__content">
                          <div v-if="!isAnyAttributesSet">
                            {{ $t('messages.noSettings') }}
                          </div>
                          <template v-for="(metadata,metadataIndex) in attributesMetadataHasValuesWithoutUrl" :key="metadata.groupDisplayName">
                            <div v-if="isGroupDataSet(metadata.groupData)" class="attributeGroup">
                              <div class="attributeGroup__groupDisplayName">
                                {{ metadata.groupDisplayName }}
                              </div>
                              <div class="attributeGroup__attributes">
                                <template v-for="(groupData,key) in metadata.groupData" :key="`${metadataIndex}_${groupData.attributeId}`">
                                  <span v-if="groupData.valueType === gmbLocationAttributeValueTypes[0]">
                                    <span v-if="key !== 0"> / </span>
                                    {{ groupData.displayName }}
                                    <Icon :name="groupData.values[0] ? 'iconOk' : 'iconCrossRed'" />
                                  </span>
                                  <span v-if="groupData.valueType === gmbLocationAttributeValueTypes[1]">
                                    <span v-if="key !== 0"> / </span>
                                    {{ groupData.displayName + ':' + extractDisplayName(groupData, groupData.values[0]) }}
                                  </span>
                                  <span v-else-if="groupData.valueType === gmbLocationAttributeValueTypes[2]">
                                    <span v-if="key !== 0"> / </span>
                                    {{ groupData.displayName + ':' }}
                                    <span v-for="value in groupData.repeatedEnumValue.setValues" :key="`${metadataIndex}_${key}_${value}_set`">
                                      {{ extractDisplayName(groupData, value) }}
                                      <Icon name="iconOk" />
                                    </span>
                                    <span v-for="value in groupData.repeatedEnumValue.unsetValues" :key="`${metadataIndex}_${key}_${value}_unset`">
                                      {{ extractDisplayName(groupData, value) }}
                                      <Icon name="iconCrossRed" />
                                    </span>
                                  </span>
                                </template>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                      <div v-if="canDisplayChangeUi && showMoreThanEditorAuthority" class="c-button c-button&#45;&#45;right">
                        <router-link
                          :to="{ name: 'CompaniesLocationStoreAttributesEdit', params: { company_uuid: companyUuid, store_uuid: storeUuid } }"
                          class="c-button__item c-button__item&#45;&#45;primary"
                        >
                          {{ $t('labels.change') }}
                        </router-link>
                      </div>
                    </div>
                  </template>
                </div>
                <div v-else class="c-info">
                  <div class="c-info__icon">
                    <Icon name="iconQuestion" />
                  </div>
                  <div class="c-info__content">
                    <p>
                      {{ $t('pages.CompaniesLocationStore.connectionWithGmb.description') }}
                      <router-link :to="{ name: 'CompaniesGoogleOauth', params: { company_uuid: companyUuid }}">{{ $t('pages.CompaniesLocationStore.connectionWithGmb.link') }}</router-link>
                    </p>
                  </div>
                </div>
              </template>
              <SiteSettingsShufoo v-else-if="displaySiteSetting === 'shufoo'" :attributes="shufooAttributes" :is-editable="canDisplayChangeUi && showMoreThanEditorAuthority" />
              <SiteSettingsMapionBiz
                v-else-if="displaySiteSetting === 'mapion_biz'"
                :attributes="mapionBizAttributes"
                :search-groups="storeOcMapionBiz.searchGroups"
                :banner-template="storeOcMapionBiz.bannerTemplate"
                :is-editable="canDisplayChangeUi && showMoreThanEditorAuthority"
                :is-display-search-group="canDisplayMapionBiz && hasAvailableSearchGroup"
                :is-display-template="canDisplayMapionBiz"
              />
              <SiteSettingsTabelog v-else-if="displaySiteSetting === 'tabelog'" :attributes="tabelogAttributes" :is-editable="canDisplayChangeUi && showMoreThanEditorAuthority" />
            </template>
          </div>
        </section>
      </div>
      <div v-show="selectedMenu === 'option'" class="p-box__body">
        <div class="c-grid">
          <div class="c-grid__items">
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__12 c-grid__item__l__12">
              <section class="p-box">
                <header class="p-box__header">
                  <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStore.optionSetting.name') }}</h3>
                </header>
                <div class="p-box__body">
                  <Loading v-show="storeOptionLoading" />
                  <template v-if="store_option && !storeOptionLoading">
                    <div class="p-box__info">
                      <div class="c-info">
                        <div class="c-info__icon">
                          <Icon name="iconQuestion" />
                        </div>
                        <div class="c-info__content">
                          <p>{{ $t('pages.CompaniesLocationStore.optionSetting.description') }}</p>
                        </div>
                      </div>
                    </div>
                    <form>
                      <div class="p-formGrid p-formGrid--border">
                        <div class="p-formGrid__items">
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <label class="c-formLabel">{{ $t('models.storeOption.franchisee') }}</label>

                            <KkSuggestField
                              id="form_input_franchisee_id"
                              :value="inputValFranchisee"
                              :placeholder="$t('messages.notSelected')"
                              :suggests="selectableFranchisees"
                              :disabled="!showMoreThanEditorAuthority"
                              @select-suggest="selectFranchiseeSuggest"
                              @close-suggest="handleSuggestFieldClose($event, 'inputFranchisee')"
                              @focus-field="handleSuggestFieldFocus($event, 'inputFranchisee')"
                              @input-field="handleInputValForm($event, 'inputFranchisee')"
                            >
                              <template #suggest="slotProps">
                                <KkSurveySelectOption :survey="slotProps.item" />
                              </template>
                            </KkSuggestField>

                            <div v-if="showMoreThanEditorAuthority" class="info c-info">
                              <div class="c-info__icon">
                                <Icon name="iconQuestion" />
                              </div>
                              <div class="c-info__content">
                                <p>
                                  {{ $t('pages.CompaniesLocationStore.optionSetting.info.franchisees.contents') }}
                                  <router-link :to="{ name: 'CompaniesManagementFranchisees', params: { company_uuid: companyUuid }}">
                                    {{ $t('pages.CompaniesLocationStore.optionSetting.info.franchisees.link') }}
                                  </router-link>
                                </p>
                              </div>
                            </div>
                          </fieldset>
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <label class="c-formLabel">{{ $t('models.storeOption.areaManager') }}</label>


                            <KkSuggestField
                              id="form_input_area_manager_id"
                              :value="inputValAreaManager"
                              :placeholder="$t('messages.notSelected')"
                              :suggests="selectableUserCompanies"
                              :disabled="!showMoreThanEditorAuthority"
                              @select-suggest="selectUserCompanySuggest"
                              @close-suggest="handleSuggestFieldClose($event, 'inputAreaManager')"
                              @focus-field="handleSuggestFieldFocus($event, 'inputAreaManager')"
                              @input-field="handleInputValForm($event, 'inputAreaManager')"
                            >
                              <template #suggest="slotProps">
                                <KkSurveySelectOption :survey="slotProps.item" />
                              </template>
                            </KkSuggestField>

                            <div v-if="showMoreThanEditorAuthority" class="info c-info">
                              <div class="c-info__icon">
                                <Icon name="iconQuestion" />
                              </div>
                              <div class="c-info__content">
                                <p>
                                  {{ $t('pages.CompaniesLocationStore.optionSetting.info.members.contents') }}
                                  <router-link :to="{ name: 'CompaniesManagementMembers', params: { company_uuid: companyUuid }}">
                                    {{ $t('pages.CompaniesLocationStore.optionSetting.info.members.link') }}
                                  </router-link>
                                </p>
                              </div>
                            </div>
                          </fieldset>
                          <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <label class="c-formLabel">{{ $t('models.storeOption.area') }}</label>

                            <KkSuggestField
                              id="form_input_area_id"
                              :value="inputValArea"
                              :placeholder="$t('messages.notSelected')"
                              :suggests="selectableAreas"
                              :disabled="!showMoreThanEditorAuthority"
                              @select-suggest="selectAreaSuggest"
                              @close-suggest="handleSuggestFieldClose($event, 'inputArea')"
                              @focus-field="handleSuggestFieldFocus($event, 'inputArea')"
                              @input-field="handleInputValForm($event, 'inputArea')"
                            >
                              <template #suggest="slotProps">
                                <KkSurveySelectOption :survey="slotProps.item" />
                              </template>
                            </KkSuggestField>

                            <div v-if="showMoreThanEditorAuthority" class="info c-info">
                              <div class="c-info__icon">
                                <Icon name="iconQuestion" />
                              </div>
                              <div class="c-info__content">
                                <p>
                                  {{ $t('pages.CompaniesLocationStore.optionSetting.info.area.contents') }}
                                  <router-link :to="{ name: 'CompaniesManagementAreas', params: { company_uuid: companyUuid }}">
                                    {{ $t('pages.CompaniesLocationStore.optionSetting.info.area.link') }}
                                  </router-link>
                                </p>
                              </div>
                            </div>
                          </fieldset>
                          <fieldset v-for="tagGroup in originalTagGroups" :key="tagGroup.kind" class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                            <label class="c-formLabel">{{ tagGroup.name }}</label>

                            <KkSuggestField
                              :id="'form_input_tagGroup_' + tagGroup.kind"
                              :value="inputValOriginalTag[tagGroup.kind]"
                              :placeholder="$t('messages.notSelected')"
                              :suggests="selectableOriginalTags(tagGroup, inputValOriginalTag[tagGroup.kind])"
                              :disabled="!showMoreThanEditorAuthority"
                              @select-suggest="selectOriginalTagSuggest"
                              @close-suggest="handleSuggestFieldClose($event, tagGroup.kind)"
                              @focus-field="handleSuggestFieldFocus($event, tagGroup.kind)"
                              @input-field="handleInputValForm($event, tagGroup.kind)"
                            >
                              <template #suggest="slotProps">
                                <KkSurveySelectOption :survey="slotProps.item" />
                              </template>
                            </KkSuggestField>

                            <div v-if="showMoreThanEditorAuthority" class="info c-info">
                              <div class="c-info__icon">
                                <Icon name="iconQuestion" />
                              </div>
                              <div class="c-info__content">
                                <p>
                                  {{ $t('pages.CompaniesLocationStore.optionSetting.info.tagGroup.contents') }}
                                  <router-link :to="{ name: 'CompaniesManagementOriginalTagGroupsEdit', params: { tag_group_kind: tagGroup.kind }}">
                                    {{ tagGroup.name + $t('pages.CompaniesLocationStore.optionSetting.info.tagGroup.link') }}
                                  </router-link>
                                </p>
                              </div>
                            </div>
                          </fieldset>
                        </div>
                      </div>
                      <div v-if="showMoreThanEditorAuthority" class="c-button c-button--right">
                        <button class="c-button__item c-button__item--primary" @click.prevent="handleStoreOptionSubmit">
                          {{ $t('labels.save') }}
                        </button>
                      </div>
                    </form>
                  </template>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <div v-show="selectedMenu === 'option'" class="p-box__body">
        <div class="c-grid">
          <div class="c-grid__items">
            <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__12 c-grid__item__l__12">
              <section class="p-box">
                <header class="p-box__header">
                  <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStore.storeCodeSetting.name') }}</h3>
                </header>
                <div class="p-box__body">
                  <Loading v-show="storeOptionLoading" />
                  <template v-if="store_option && !storeOptionLoading">
                    <ValidationObserver ref="storeOptionForm">
                      <form>
                        <div class="p-formGrid p-formGrid--border">
                          <div class="p-formGrid__items">
                            <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                              <label class="c-formLabel">{{ $t('models.storeOption.code') }}</label>
                              <ValidationProvider v-slot="{ errors, value, handleChange }"
                                                  key="code_validation"
                                                  v-model="store_option.attributes.code"
                                                  :name="$t('models.store.code')"
                                                  rules="max:100"
                              >
                                <input :disabled="!showMoreThanEditorAuthority" :value="value" type="text" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                                <ValidationError :error="errors[0]" />
                              </ValidationProvider>
                            </fieldset>
                          </div>
                        </div>
                        <div v-if="showMoreThanEditorAuthority" class="c-button c-button--right">
                          <button class="c-button__item c-button__item--primary" @click.prevent="handleStoreOptionSubmit">
                            {{ $t('labels.save') }}
                          </button>
                        </div>
                      </form>
                    </ValidationObserver>
                  </template>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
      <div v-show="selectedMenu === 'sns'" class="p-box__body">
        <section class="p-box">
          <header class="p-box__header">
            <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStore.socialSetting.title') }}</h3>
          </header>
          <div class="p-box__body">
            <div class="p-box__info">
              <div class="c-info">
                <div class="c-info__icon">
                  <Icon name="iconQuestion" />
                </div>
                <div class="c-info__content">
                  <p>{{ $t('pages.CompaniesLocationStore.socialSetting.description') }}</p>
                </div>
              </div>
            </div>
            <div class="p-formGrid p-formGrid--border">
              <Loading v-if="store_loading || storeSocialLoading" />
              <div v-else class="p-formGrid__items">
                <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                  <label class="c-formLabel">
                    <div class="c-icon c-icon--facebook" :class="!facebookUrl && 'is_disabled'">
                      <Icon name="iconFacebook" />
                    </div>
                    <p>Facebook（URL）</p>
                  </label>
                  <KkTooltip
                    :is-visible="isHoveredFacebookUrlDescription"
                    top="25px"
                    width="500px"
                    left="0px"
                    :label="$t('pages.LocationStoreConnectAccount.info.snsUrl')"
                    font-weight="bold"
                    font-size="12px"
                    class="formLabel"
                    @showTooltip="isHoveredFacebookUrlDescription=true"
                    @hideTooltip="isHoveredFacebookUrlDescription=false"
                  >
                    <p>{{ $t('pages.LocationStoreConnectAccount.info.exampleUrl') }}</p>
                    <ul>
                      <li v-for="url in siteInfoMap['facebook']['sampleURLs']" :key="url" class="sampleUrls">
                        {{ url }}
                      </li>
                    </ul>
                  </KkTooltip>
                  <ValidationProvider v-slot="{ errors, value, handleChange }"
                                      key="facebook_url_validation"
                                      v-model="facebookUrl"
                                      rules="facebook_url"
                                      name="facebook_url"
                  >
                    <input :disabled="!showMoreThanEditorAuthority" :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                    <ValidationError :error="errors[0]" />
                  </ValidationProvider>
                </fieldset>
                <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                  <label class="c-formLabel">
                    <div class="c-icon c-icon--twitter" :class="!twitterUrl && 'is_disabled'">
                      <Icon name="iconTwitter" />
                    </div>
                    <p>X（URL）</p>
                  </label>
                  <KkTooltip
                    :is-visible="isHoveredTwitterUrlDescription"
                    top="25px"
                    width="500px"
                    left="0px"
                    :label="$t('pages.LocationStoreConnectAccount.info.snsUrl')"
                    font-weight="bold"
                    font-size="12px"
                    class="formLabel"
                    @showTooltip="isHoveredTwitterUrlDescription=true"
                    @hideTooltip="isHoveredTwitterUrlDescription=false"
                  >
                    <p>{{ $t('pages.LocationStoreConnectAccount.info.exampleUrl') }}</p>
                    <ul>
                      <li v-for="url in siteInfoMap['twitter']['sampleURLs']" :key="url">
                        {{ url }}
                      </li>
                    </ul>
                  </KkTooltip>
                  <ValidationProvider v-slot="{ errors, value, handleChange }"
                                      key="twitter_url_validation"
                                      v-model="twitterUrl"
                                      rules="twitter_url"
                                      name="twitter_url"
                  >
                    <input :disabled="!showMoreThanEditorAuthority" :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                    <ValidationError :error="errors[0]" />
                  </ValidationProvider>
                </fieldset>
                <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                  <label class="c-formLabel">
                    <div class="c-icon c-icon--instagram" :class="!instagramUrl && 'is_disabled'">
                      <Icon name="iconInstagram" />
                    </div>
                    <p>Instagram（URL）</p>
                  </label>
                  <KkTooltip
                    :is-visible="isHoveredInstagramUrlDescription"
                    top="25px"
                    width="500px"
                    left="0px"
                    :label="$t('pages.LocationStoreConnectAccount.info.snsUrl')"
                    font-weight="bold"
                    font-size="12px"
                    class="formLabel"
                    @showTooltip="isHoveredInstagramUrlDescription=true"
                    @hideTooltip="isHoveredInstagramUrlDescription=false"
                  >
                    <p>{{ $t('pages.LocationStoreConnectAccount.info.exampleUrl') }}</p>
                    <ul>
                      <li v-for="url in siteInfoMap['instagram']['sampleURLs']" :key="url">
                        {{ url }}
                      </li>
                    </ul>
                  </KkTooltip>
                  <ValidationProvider v-slot="{ errors, value, handleChange }"
                                      key="instagram_url_validation"
                                      v-model="instagramUrl"
                                      rules="instagram_url"
                                      name="instagram_url"
                  >
                    <input :disabled="!showMoreThanEditorAuthority" :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                    <ValidationError :error="errors[0]" />
                  </ValidationProvider>
                </fieldset>
                <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                  <label class="c-formLabel">
                    <div class="c-icon c-icon--line" :class="!lineUrl && 'is_disabled'">
                      <Icon name="iconLine" />
                    </div>
                    <p>LINE（URL）</p>
                  </label>
                  <KkTooltip
                    :is-visible="isHoveredLineUrlDescription"
                    top="25px"
                    width="500px"
                    left="0px"
                    :label="$t('pages.LocationStoreConnectAccount.info.snsUrl')"
                    font-weight="bold"
                    font-size="12px"
                    class="formLabel"
                    @showTooltip="isHoveredLineUrlDescription=true"
                    @hideTooltip="isHoveredLineUrlDescription=false"
                  >
                    <p>{{ $t('pages.LocationStoreConnectAccount.info.exampleUrl') }}</p>
                    <ul>
                      <li v-for="url in siteInfoMap['line']['sampleURLs']" :key="url">
                        {{ url }}
                      </li>
                    </ul>
                  </KkTooltip>
                  <ValidationProvider v-slot="{ errors, value, handleChange }"
                                      key="line_url_validation"
                                      v-model="lineUrl"
                                      rules="line_url"
                                      name="line_url"
                  >
                    <input :disabled="!showMoreThanEditorAuthority" :value="value" class="c-textField" :placeholder="$t('labels.placeholder.basic')" @input="handleChange">
                    <ValidationError :error="errors[0]" />
                  </ValidationProvider>
                </fieldset>
              </div>
            </div>
            <div v-if="showMoreThanEditorAuthority" class="c-button c-button--right">
              <button class="c-button__item c-button__item--primary" @click.prevent="handleStoreSocialSubmit">
                {{ $t('labels.save') }}
              </button>
            </div>
          </div>
        </section>
      </div>
      <div v-show="selectedMenu === 'competitor'" class="p-box__body">
        <Loading v-show="competitorsLoading || store_loading" />
        <template v-if="competitors && !competitorsLoading && store && !store_loading">
          <Competitors
            v-if="providedFeaturesCompetitor"
            :competitors="competitors"
            :can-add-competitor="store.attributes.can_add_competitor"
            :loading="competitorsLoading"
            :is-editable="showMoreThanEditorAuthority"
            @delete="handleDeleteCompetitorSubmit"
          />
        </template>
      </div>
      <div v-show="selectedMenu === 'foodMenu'" class="p-box__body">
        <Loading v-show="foodMenuLoading || gmbMetadataLoading" />
        <template v-if="selectedFoodMenu && foodMenus && !foodMenuLoading && !gmbMetadataLoading">
          <FoodMenu
            :display-food-menu="displayFoodMenu"
            :food-menus="foodMenus"
            :show-only-owner-authority="showOnlyOwnerAuthority"
            :is-docomo-plan="isDocomoPlan"
            @change-food-menu="changeFoodMenu"
            @update-store-food-menu="handleUpdateStoreFoodMenu"
          />
        </template>
      </div>
      <div v-show="selectedMenu === 'product'" class="p-box__body">
        <ProductList
          v-if="hasAvailableProduct"
          :company-uuid="currentCompanyUuid"
          :store-uuid="currentStoreUuid"
          :is-editable="showMoreThanEditorAuthority"
        />
      </div>
      <div v-show="selectedMenu === 'service'" class="p-box__body">
        <Loading v-if="serviceLoading || !storeServicePatterns || !categoriesWithServicePatterns || gmbMetadataLoading" />
        <template v-else>
          <Services
            :categories="categoriesWithServicePatterns"
            :store-service-patterns="storeServicePatterns"
            :gmb-no-linked="gmbNoLinked"
            :show-only-owner-authority="showOnlyOwnerAuthority"
            @update-store-service-pattern="handleUpdateStoreServicePattern"
          />
        </template>
      </div>
    </div>

    <ConfirmationModal
      :show-confirmation-modal="showConfirmationModal"
      @on-submit="handleGmbStoreSubmit()"
      @close-modal="toggleConfirmationModal()"
    />
    <PastSpecialHoursModal
      v-if="showPastSpecialHoursModal"
      :is-visible="showPastSpecialHoursModal"
      :date-times="pastSpecialHours"
      @close="showPastSpecialHoursModal = false"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import MediaLibraries from '../components/CompaniesLocationStore/MediaLibraries'
import ConfirmationModal from '../components/CompaniesLocationStore/ConfirmationModal'
import Competitors from '../components/CompaniesLocationStore/Competitors'
import FoodMenu from '../components/CompaniesLocationStore/FoodMenu'
import Loading from '../components/Loading.vue'
import ValidationError from '../components/ValidationError'
import Star from '../components/Star.vue'
import { externalAccountsConst } from '../modules/externalAccountsConst'
import Icon from '../components/Icon.vue'
import {
  KkIcon,
  KkTooltip,
} from '../components/base'
import BusinessHoursCrawlerStatus from '../components/CompaniesLocationStore/BusinessHoursCrawlerStatus.vue'
import PastSpecialHoursModal from '../components/CompaniesLocationStore/PastSpecialHoursModal.vue'
import iconMapMarkerhPath from '../../../images/common/icon_map-marker.svg'
import iconPhonePath from '../../../images/common/icon_phone.svg'
import iconWebsiteLinkPath from '../../../images/common/icon_website_link.svg'
import cardVisaPath from '../../../images/common/card_visa.svg'
import cardAmericanExpressPath from '../../../images/common/card_american_express.svg'
import cardMastercardPath from '../../../images/common/card_mastercard.svg'
import cardDiscoverPath from '../../../images/common/card_discover.svg'
import cardDinersclubPath from '../../../images/common/card_dinersclub.svg'
import cardJcbPath from '../../../images/common/card_jcb.svg'
import cardUnionPayPath from '../../../images/common/card_union_pay.svg'
// import Autocomplete from '../components/Autocomplete'
import Select from '../components/Select'
import { google } from '../modules/sitesConst'
import { siteInfoMap } from '../modules/sitesInfoMapConst'
import ProductList from '../components/CompaniesLocationStore/ProductList'
import { gmbLocationAttributeValueTypes } from '../stores/companies/store/gmb/attributes'
import Info from '../components/Info'
import SiteSettingsShufoo from '../components/CompaniesLocationStore/SiteSettings/Shufoo.vue'
import SiteSettingsMapionBiz from '../components/CompaniesLocationStore/SiteSettings/MapionBiz.vue'
import SiteSettingsTabelog from '../components/CompaniesLocationStore/SiteSettings/Tabelog.vue'
import Services from '../components/CompaniesLocationStore/Services'
import { displayHour, week } from '../modules/regularHour'
import { formatProps, KkSiteList } from 'src/biz_spa/domains/locationStore'
import { KkSuggestField, KkInfo } from '../components/ui'
import { KkSurveySelectOption } from '../components/domains/customSurveys'
import { KkList as KkPaymentMethodList } from '../domains/paymentMethod'
import { KkPreviewModal } from '../components/domains/location/store/businessHour'
import { KkPreviewModal as KkWebsiteLinkPreviewModal } from '../components/domains/location/store/websiteLink'

export default {
  name: 'CompaniesLocationStore',
  components: {
    KkSiteList,
    Info,
    ConfirmationModal,
    Competitors,
    FoodMenu,
    MediaLibraries,
    Loading,
    Icon,
    KkIcon,
    KkTooltip,
    BusinessHoursCrawlerStatus,
    PastSpecialHoursModal,
    Star,
    ValidationError,
    // Autocomplete,
    Select,
    ProductList,
    SiteSettingsShufoo,
    SiteSettingsMapionBiz,
    SiteSettingsTabelog,
    Services,
    KkSuggestField,
    KkSurveySelectOption,
    KkPaymentMethodList,
    KkPreviewModal,
    KkWebsiteLinkPreviewModal,
    KkInfo,
  },
  emits: ['update-title'],
  data () {
    return {
      companyUuid: this.$route.params.company_uuid,
      storeUuid: this.$route.params.store_uuid,
      iconMapMarkerhPath,
      iconPhonePath,
      iconWebsiteLinkPath,
      cardVisaPath,
      cardAmericanExpressPath,
      cardMastercardPath,
      cardDiscoverPath,
      cardDinersclubPath,
      cardJcbPath,
      cardUnionPayPath,
      showConfirmationModal: false,
      isHoveredFacebookUrlDescription: false,
      isHoveredTwitterUrlDescription: false,
      isHoveredInstagramUrlDescription: false,
      isHoveredLineUrlDescription: false,
      selectedMenu: 'information',
      displayAllSites: false,
      title: '',
      cacheStoreValues: {
        inputFranchisee: '',
        inputAreaManager: '',
        inputArea: '',
        a: '',
        b: '',
        c: '',
        d: '',
        e: '',
      },
      externalAccountsConst,
      selectedBusinessGroupId: null,
      google,
      siteInfoMap,
      shufooId: '',
      mapionBizId: '',
      displaySiteSetting: 'google',
      currentUser: gon.currentUser,
      facebookUrl: '',
      twitterUrl: '',
      instagramUrl: '',
      lineUrl: '',
      showPastSpecialHoursModal: false,
      showBusinessHourPreview: false,
      showWebsiteLinkPreview: false
    }
  },
  computed: {
    ...mapState({
      company: state => state.companiesCompany.data,
      store: state => state.companiesLocationStore.data,
      store_loading: state => state.companiesLocationStore.loading,
      storeBusinessHoursMeta: state => state.companiesStoreBusinessHours.meta,
      storeRegularHoursLoading: state => state.companiesStoreBusinessHours.loading,
      storeBusinessHoursCrawlers: state => state.companiesStoreBusinessHoursCrawlers.data,
      storeBusinessHoursCrawlersLoading: state => state.companiesStoreBusinessHoursCrawlers.loading,
      payment_methods: state => state.companiesStorePaymentMethods.data,
      payment_methods_loading: state => state.companiesStorePaymentMethods.loading,
      paymentMethodsPreview: state => state.companiesStorePaymentMethods.preview,
      paymentMethodsPreviewLoading: state => state.companiesStorePaymentMethods.previewLoading,
      franchisees: state => state.companiesFranchisees.data,
      franchisees_loading: state => state.companiesFranchisees.loading,
      users: state => state.companiesUsers.data,
      users_loading: state => state.companiesUsers.loading,
      sites: state => state.companiesSites.data,
      sites_loading: state => state.companiesSites.loading,
      store_option: state => state.companiesStoreOption.data,
      store_option_loading: state => state.companiesStoreOption.loading,
      storeSocial: state => state.companiesStoreSocial.data,
      storeSocialLoading: state => state.companiesStoreSocial.loading,
      gmb_locations: state => state.companiesStoreGmbLocations.data,
      gmb_locations_loading: state => state.companiesStoreGmbLocations.loading,
      store_company: state => state.companiesCompany.data,
      store_company_loading: state => state.companiesCompany.loading,
      store_gmb: state => state.companiesStoreGmb.data,
      store_gmb_connected: state => state.companiesStoreGmb.connected,
      store_gmb_loading: state => state.companiesStoreGmb.loading,
      store_gmb_loading_for_update: state => state.companiesStoreGmb.loadingForUpdate,
      storeExternalAccounts: state => state.companiesStoreExternalAccounts.data,
      storeExternalAccountsLoading: state => state.companiesStoreExternalAccounts.loading,
      areas: state => state.companiesAreas.data,
      areas_loading: state => state.companiesAreas.loading,
      business_groups: state => state.companiesGmbAccounts.data,
      business_groups_loading: state => state.companiesGmbAccounts.loading,
      competitors: state => state.companiesStoreCompetitors.data,
      competitorsLoading: state => state.companiesStoreCompetitors.loading,
      gmbOpenInfoLoading: state => state.companiesStoreGmbOpenInfo.loading,
      originalTagGroups: state => state.companiesOriginalTagGroups.data,
      originalTagGroupsLoading: state => state.companiesOriginalTagGroups.loading,
      storeFoodMenu: state => state.companiesStoreFoodMenu.data,
      storeFoodMenuLoading: state => state.companiesStoreFoodMenu.loading,
      foodMenus: state => state.companiesFoodMenus.data,
      foodMenusLoading: state => state.companiesFoodMenus.loading,
      selectedFoodMenu: state => state.companiesFoodMenu.data,
      selectedFoodMenuLoading: state => state.companiesFoodMenu.loading,
      mediaLibraries: state => state.companiesStoreMediaLibraries.data,
      mediaLibrariesMeta: state => state.companiesStoreMediaLibraries.meta,
      storeMediaLibrariesLoading: state => state.companiesStoreMediaLibraries.loading,
      storeGmbCategories: state => state.companiesStoreGmbCategories.data,
      storeGmbAttributesLoading: state => state.companiesStoreGmbAttributes.loading,
      storeGmbFoodorderings: state => state.companiesStoreGmbFoodorderings.data,
      storeGmbFoodorderingsLoading: state => state.companiesStoreGmbFoodorderings.loading,
      storeGmbCategoriesLoading: state => state.companiesStoreGmbCategories.loading,
      storeOcShufooLoading: state => state.companiesStoreOcShufoo.loading,
      storeOcShufoo: state => state.companiesStoreOcShufoo.data,
      shufooAttributes: state => state.companiesStoreOcShufooAttributes.data,
      shufooAttributesLoading: state => state.companiesStoreOcShufooAttributes.loading,
      storeOcMapionBizLoading: state => state.companiesStoreOcMapionBiz.loading,
      storeOcMapionBiz: state => state.companiesStoreOcMapionBiz.data,
      mapionBizAttributes: state => state.companiesStoreOcMapionBizAttributes.data,
      mapionBizAttributesLoading: state => state.companiesStoreOcMapionBizAttributes.loading,
      tabelogAttributes: state => state.companiesStoreTabelogAttributes.data,
      tabelogAttributesLoading: state => state.companiesStoreTabelogAttributes.loading,
      categoriesWithServicePatterns: state => state.companiesStoreGmbCategoriesWithServicePatterns.data,
      storeServicePatterns: state => state.companiesStoreServicePatterns.data,
      servicePatternsLoading: state => state.companiesStoreServicePatterns.loading,
      categoriesWithServicePatternsLoading: state => state.companiesStoreGmbCategoriesWithServicePatterns.loading,
      gmbMetadataLoading: state => state.companiesStoreGmbMetadata.loading,
    }),
    ...mapGetters({
      syncedAll: 'companiesStoreGmb/syncedAll',
      gmbLocationSynced: 'companiesStoreGmb/gmbLocationSynced',
      providedFeaturesCompetitor: 'companiesCompany/providedFeaturesCompetitor',
      hasAvailableProduct: 'companiesCompany/hasAvailableProduct',
      hasAvailableSearchGroup: 'companiesCompany/hasAvailableSearchGroup',
      storeGmbAttributeMetadata: 'companiesStoreGmbAttributes/attributeMetadata',
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
      showMoreThanOperatorAuthority: 'companiesAccessibleApi/showMoreThanOperatorAuthority',
      gmbOpenInfoStatus: 'companiesStoreGmbOpenInfo/gmbOpenInfoStatus',
      attributesMetadataHasValuesWithoutUrl: 'companiesStoreGmbAttributes/attributesMetadataHasValuesWithoutUrl',
      urlGroupDataFlattenList: 'companiesStoreGmbAttributes/urlGroupDataFlattenList',
      isAnyAttributesSet: 'companiesStoreGmbAttributes/isAnyAttributesSet',
      providedFeaturesLinkUrl: 'companiesCompany/providedFeaturesLinkUrl',
      providedFeaturesExternalAccount: 'companiesCompany/providedFeaturesExternalAccount',
      providedFeaturesProduct: 'companiesCompany/providedFeaturesProduct',
      providedFeaturesFoodMenu: 'companiesCompany/providedFeaturesFoodMenu',
      providedFeaturesServicePattern: 'companiesCompany/providedFeaturesServicePattern',
      providedFeaturesBusinessHoursCrawler: 'companiesCompany/providedFeaturesBusinessHoursCrawler',
      providedFeaturesMediaLibrary: 'companiesCompany/providedFeaturesMediaLibrary',
      providedFeaturesGmb: 'companiesCompany/providedFeaturesGmb',
      hasSettableAttributes: 'companiesStoreGmbAttributes/hasSettableAttributes',
      canDisplayChangeUi: 'companiesCompany/canDisplayChangeUi',
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
      providedFeaturesPaymentMethod: 'companiesCompany/providedFeaturesPaymentMethod',
      providedFeaturesStoreOption: 'companiesCompany/providedFeaturesStoreOption',
      providedFeaturesStoreSocial: 'companiesCompany/providedFeaturesStoreSocial',
      providedFeaturesOriginalTagGroup: 'companiesCompany/providedFeaturesOriginalTagGroup',
      providedFeaturesFranchisee: 'companiesCompany/providedFeaturesFranchisee',
      providedFeaturesArea: 'companiesCompany/providedFeaturesArea',
      canModifyServiceList: 'companiesStoreGmbMetadata/canModifyServiceList',
      hasGmbLocation: 'companiesStoreGmb/hasGmbLocation',
      storeMoreHours: 'companiesStoreBusinessHours/displayMoreHours',
      isRegularHourSet: 'companiesStoreBusinessHours/isRegularHourSet',
      displayRegularHours: 'companiesStoreBusinessHours/displayRegularHours',
      storeRegularHours: 'companiesStoreBusinessHours/regularHours',
      storeHolidayRegularHour: 'companiesStoreBusinessHours/holidayRegularHour',
      storeDayBeforeHolidayRegularHour: 'companiesStoreBusinessHours/dayBeforeHolidayRegularHour',
      storeWdayIndexRegularHours: 'companiesStoreBusinessHours/wdayIndexRegularHours',
      storeRegularHourAdditionalDescription: 'companiesStoreBusinessHours/regularHourAdditionalDescription',
      storeRegularHolidayAdditionalDescription: 'companiesStoreBusinessHours/regularHolidayAdditionalDescription',
      storeSpecialHours: 'companiesStoreBusinessHours/specialHours',
      futureSpecialHours: 'companiesStoreBusinessHours/futureSpecialHours',
      pastSpecialHours: 'companiesStoreBusinessHours/pastSpecialHours',
      inputValFranchisee: 'companiesStoreOption/inputValFranchisee',
      inputValAreaManager: 'companiesStoreOption/inputValAreaManager',
      inputValArea: 'companiesStoreOption/inputValArea',
      inputValOriginalTag: 'companiesStoreOption/inputValOriginalTag',
      canHaveFoodMenus: 'companiesCompany/canHaveFoodMenus',
      hasDocomoStore: 'companiesCompany/hasDocomoStore',
      isRettyPlan: 'companiesCompany/isRettyPlan',
    }),
    weeks(){
      return week
    },
    gmbUrlAttributesDisplayNames() {
      let text = ''
      const separator = '・'
      if (this.urlGroupDataFlattenList.length) {
        text += this.urlGroupDataFlattenList.map(v => v.displayName).join(separator)
        text += separator
      }
      return text
    },
    gmbLocationAttributeValueTypes() {
      return gmbLocationAttributeValueTypes
    },
    isCategorySet() {
      return this.storeGmbCategories && this.storeGmbCategories.store_gmb_categories.length !== 0
    },
    currentCompanyUuid() {
      const company_uuid = this.$route.params.company_uuid

      return company_uuid
    },
    currentStoreUuid() {
      return this.$route.params.store_uuid
    },
    sitesDisplayedOnBasicInfo() {
      if (!this.store) return []

      if (!this.sites) {
        const results = []
        results.push({siteType: 'google_map', isDisabled: !this.store_gmb_connected })
        results.push({siteType: 'shufoo', isDisabled: !this.store.attributes.shufoo_connected })
        results.push({siteType: 'mapion_biz', isDisabled: !this.store.attributes.mapion_biz_connected })
        if (this.hasDocomoStore) results.push({siteType: 'docomo', isDisabled: !this.store.attributes.docomo_connected })
        return formatProps({ sites: results, storeGmbConnected: this.store_gmb_connected })
      }

      const businessTypeId = this.store.attributes.business_type_id
      const displaySites = this.sites.filter(site => site.businessTypes.includes(businessTypeId) || site.url)

      const results = displaySites
        .map(site => ({siteType: site.siteType, isDisabled: !site.url}))
        .filter(site => site.siteType !== 'yahoo_place')
      results.push({siteType: 'shufoo', isDisabled: !this.store.attributes.shufoo_connected })
      results.push({siteType: 'mapion_biz', isDisabled: !this.store.attributes.mapion_biz_connected })
      if(this.hasDocomoStore) results.push({siteType: 'docomo', isDisabled: !this.store.attributes.docomo_connected })
      return formatProps({ sites: results, storeGmbConnected: this.store_gmb_connected })
    },
    filtered_sites() {
      if(!this.providedFeaturesLinkUrl) return []
      const businessTypeId = this.store?.attributes?.business_type_id

      // selectだと文字列boolがセットされる
      return this.displayAllSites === 'true' ? this.sites : this.sites.filter(site => site.businessTypes.includes(businessTypeId))
    },
    has_gmb_account() {
      return !!this.store_company?.attributes?.has_gmb_account
    },
    businessGroupsCollection() {
      const obj = {}
      this.business_groups.data.forEach(v => {
        obj[v.attributes.name] = v.id
      })
      return obj
    },
    linkedSites() {
      const sites = this.store_gmb_connected ? ['google'] : []
      const externalAccountSites = this.storeExternalAccounts?.map(account => account.attributes.service_type)
        // TODO:: バックエンドからyahooPlaceを返してもらったら外す
        .map(v => v === 'yahooLoco' ? 'yahooPlace' : v)
      return sites.concat(externalAccountSites).filter(v => v)
    },
    siteSettingsSelects() {
      const obj = {}
      obj[this.$t('labels.sites.google')] = 'google'
      if(this.store.attributes.shufoo_connected) obj[this.$t('labels.sites.shufoo')] = 'shufoo'
      if(this.store.attributes.mapion_biz_connected) obj[this.$t('labels.sites.mapionBiz')] = 'mapion_biz'
      if(this.storeExternalAccounts?.find(e => e.attributes.service_type === 'tabelog')) obj[this.$t('labels.sites.tabelog')] = 'tabelog'
      return obj
    },
    filterGmbLocations() {
      return this.gmb_locations.map((location) => {
        const obj = {
          id: location.id,
          name: location.attributes.name,
        }
        if (location.attributes.connected) { obj.$isDisabled = true }
        return obj
      })
    },
    noSites() {
      return this.filtered_sites?.length === 0
    },
    sitesLoading() {
      return this.sites_loading || this.store_company_loading || this.store_loading
    },
    storeOptionLoading() {
      return this.store_option_loading || this.franchisees_loading || this.users_loading || this.areas_loading || this.originalTagGroupsLoading
    },
    gmbProtectionLoading() {
      return this.store_gmb_loading || this.store_gmb_loading_for_update
    },
    externalAccountsLoading() {
      return this.store_loading || this.storeExternalAccountsLoading
    },
    foodMenuLoading(){
      return this.storeFoodMenuLoading || this.foodMenusLoading || this.selectedFoodMenuLoading
    },
    mediaLibrariesLoading() {
      return this.store_gmb_loading || this.storeMediaLibrariesLoading
    },
    siteSettingLoading() {
      return this.store_gmb_loading ||
              this.gmbOpenInfoLoading ||
              this.shufooAttributesLoading ||
              this.storeOcMapionBizLoading ||
              this.mapionBizAttributesLoading ||
              this.store_loading ||
              this.tabelogAttributesLoading ||
              this.storeExternalAccountsLoading ||
              this.storeOcMapionBizLoading
    },
    serviceLoading() { return this.servicePatternsLoading || this.categoriesWithServicePatternsLoading },
    displayFoodMenu() {
      return this.selectedFoodMenu?.id ? this.selectedFoodMenu : this.storeFoodMenu
    },
    gmbNoLinked(){
      return !this.store_gmb_connected && !this.store_gmb_loading && !this.storeRegularHoursLoading
    },
    isServicePatternAvailable() { return this.providedFeaturesServicePattern && this.canModifyServiceList },
    isFoodMenuAvailable() { return this.providedFeaturesFoodMenu && this.canHaveFoodMenus },
    menus() {
      const menus = this.isRettyPlan ? ['connection'] : ['information', 'connection']
      if (this.providedFeaturesStoreOption) menus.push('option')
      if (this.providedFeaturesStoreSocial) menus.push('sns')
      if (this.providedFeaturesCompetitor) menus.push('competitor')
      if (this.isFoodMenuAvailable) menus.push('foodMenu')
      if (this.hasAvailableProduct && this.providedFeaturesProduct) menus.push('product')
      if (this.isServicePatternAvailable) menus.push('service')
      return menus
    },
    storeBusinessHoursCrawlerStatus() { return this.storeBusinessHoursCrawlers?.status },
    enabledBusinessHoursCrawler() { return this.storeBusinessHoursCrawlers?.enabled },
    displayStoreGmbOpenStatus() {
      if(this.gmbOpenInfoStatus){
        return this.$t('pages.CompaniesLocationStore.openInfoStatus.' + this.gmbOpenInfoStatus)
      }
      return this.$t('pages.CompaniesLocationStore.siteSetting.noSettings')
    },
    displayStoreGmbCategories() {
      if (!this.storeGmbCategories) return null

      const { store_gmb_categories: categories } = this.storeGmbCategories
      if (categories.length) {
        const secondaries = categories.filter(category => !category.primary)
        const primary = categories.find(category => category.primary)
        let text = primary.name
        if (secondaries.length) {
          text += ` （${ secondaries.map(secondary => secondary.name).join(' | ') }）`
        }
        return text
      } else {
        return this.$t('pages.CompaniesLocationStore.siteSetting.noSettings')
      }
    },
    hotelBrand() {
      return this.store.attributes.hotel_brand
    },
    canDisplayShufoo() {
      return this.showOnlyOwnerAuthority && (this.currentUser.admin || this.currentUser.distributer_uuid)
    },
    canDisplayMapionBiz() {
      return this.showOnlyOwnerAuthority && (this.currentUser.admin || this.currentUser.distributer_uuid)
    },
    selectableFranchisees() {
      if (!this.franchisees) { return [] }

      const filteredFranchisees =
        this.inputValFranchisee
          ? this.franchisees.filter(franchisee => franchisee.attributes.name.includes(this.inputValFranchisee))
          : this.franchisees

      return [
        {
          'id': null,
          'name': this.$t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label'),
          'franchiseeName': null
        },
        ...filteredFranchisees.map(franchisee => {
          return {
            id: franchisee.id,
            name: franchisee.attributes.name,
            franchiseeName: franchisee.attributes.name
          }
        })
      ]
    },
    selectableUserCompanies() {
      if (!this.users) { return [] }

      const filteredUserCompanies =
        this.inputValAreaManager
          ? this.users.filter(areaManager => areaManager.attributes.name.includes(this.inputValAreaManager))
          : this.users

      return [
        {
          'id': null,
          'name': this.$t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label'),
          'userCompanyName': null
        },
        ...filteredUserCompanies.map(userCompany => {
          return {
            id: userCompany.id,
            name: userCompany.attributes.name,
            userCompanyName: userCompany.attributes.name
          }
        })
      ]
    },
    selectableAreas() {
      if (!this.areas) { return [] }

      const filteredAreas =
        this.inputValArea
          ? this.areas.filter(area => area.attributes.name.includes(this.inputValArea))
          : this.areas

      return [
        {
          'id': null,
          'name': this.$t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label'),
          'areaName': null
        },
        ...filteredAreas.map(area => {
          return {
            id: area.id,
            name: area.attributes.name,
            areaName: area.attributes.name
          }
        })
      ]
    },
    selectableOriginalTags() {
      return function (tagGroup, inputVal) {
        if (tagGroup.tags.length === 0) { return [] }

        let filterTags = tagGroup.tags
        if (inputVal) {
          filterTags = filterTags.filter(tag => tag.name.includes(inputVal))
        }

        const suggestTags = filterTags.map(tag => (
          {
            'id': tag.id,
            'name': tag.name,
            'tagName': tag.name,
            'kind': tagGroup.kind,
          }
        ))

        suggestTags.unshift(
          {
            'id': null,
            'name': this.$t('pages.CompaniesLocationStoresBulkEditStoreOptions.placeHolder.label'),
            'tagName': null,
            'kind': tagGroup.kind,
          }
        )
        return suggestTags
      }
    },
    webSiteUrl() {
      // 共通のURLがあればそのまま返す
      let url = this.store.attributes.web_site_url
      if (url) return url

      // 連携サイトごとにURLを設定している場合
      const siteTypes = this.store.attributes.site_types

      // googleの場合、連携されており、書き換え防止設定がされている場合はurlが未設定でも空文字で表示する
      if (siteTypes['google'] && this.gmbLocationSynced.website_url_synced) {
        return siteTypes['google'].web_site_url
      }

      // それ以外の場合は、最初にweb_site_urlが設定されている連携サイトのURLを返す
      const siteKeys = Object.keys(this.store.attributes.site_types)

      for (const siteKey of siteKeys) {
        const site = siteTypes[siteKey]

        if (site && site.web_site_url) {
          url = site.web_site_url
          break
        }
      }

      return url
    },
    showWebSiteLink() {
      if (this.isDocomoPlan) return false // DocomoPlanの場合は非表示

      for (let key in this.store.attributes.site_types) {
        const siteType = this.store.attributes.site_types[key]
        if (siteType && siteType.web_site_url && siteType.web_site_url.trim() !== '') {
          return true // 一つでもweb_site_urlに値があればtrueを返す
        }
      }

      return false
    },
  },
  watch: {
    title(newTitle) {
      this.$emit('update-title', newTitle)
    }
  },
  async created() {
    // 前回の状態が表示されないようにstoreの情報をリセット
    this.initData()
    if (this.$route.query.tab){
      this.selectedMenu = this.$route.query.tab
    } else if (this.isRettyPlan && this.selectedMenu === 'information'){
      this.$router.push({ name: 'LocationStoreConnects'})
    }
    this.resetStoreBusinessHours()
    this.resetStorePaymentMethods()
    if(this.providedFeaturesGmb){
      this.readBusinessGroups(this.companyUuid)
      this.readGmbOpenInfo()
    }
    if(this.providedFeaturesLinkUrl) this.readSites({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    if(this.providedFeaturesMediaLibrary) this.readMediaLibraries({ storeUuid: this.storeUuid })
    if(this.providedFeaturesFranchisee) this.readFranchisees(this.companyUuid)
    this.readUsers({ company_uuid: this.companyUuid, except_paginate: true })
    if(this.providedFeaturesExternalAccount) {
      // readExternalAccountsの取得値によって挙動が変わる処理はthen内で定義
      this.readExternalAccounts({ company_uuid: this.companyUuid, store_uuid: this.storeUuid }).then(() => {
        if(this.storeExternalAccounts?.find(e => e.attributes.service_type === 'tabelog')) {
          this.readStoreTabelogAttributes()
        }
      })
    }
    if(this.providedFeaturesArea) this.readAreas(this.companyUuid)
    if(this.providedFeaturesOriginalTagGroup) this.readOriginalTagGroups()
    const actions = [this.fetchStoreInfo()]
    if(this.providedFeaturesGmb) actions.push(this.readStoreGmbCategories({ companyUuid: this.companyUuid, storeUuid: this.storeUuid }))
    await Promise.all(actions)
    if(this.store.attributes.shufoo_connected) {
      this.readStoreOcShufoo()
      this.readStoreOcShufooAttributes()
    }

    if(this.store.attributes.mapion_biz_connected) {
      this.readStoreOcMapionBiz()
      this.readStoreOcMapionBizAttributes()
    }

    if(this.providedFeaturesCompetitor) this.readCompetitors({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    if (this.isCategorySet) { this.readGmbAttributes() }
    this.readGmbFoodorderings()
  },
  methods: {
    ...mapActions({
      readStore: 'companiesLocationStore/readStore',
      readStoreOption: 'companiesStoreOption/readStoreOption',
      updateStoreOption: 'companiesStoreOption/updateStoreOption',
      readStoreSocial: 'companiesStoreSocial/readStoreSocial',
      updateStoreSocial: 'companiesStoreSocial/updateStoreSocial',
      readFranchisees: 'companiesFranchisees/readFranchisees',
      readUsers: 'companiesUsers/readUsers',
      readStoreBusinessHours: 'companiesStoreBusinessHours/readStoreBusinessHours',
      resetStoreBusinessHours: 'companiesStoreBusinessHours/resetData',
      readStoreBusinessHoursCrawlers: 'companiesStoreBusinessHoursCrawlers/readStoreBusinessHoursCrawlers',
      readStorePaymentMethods: 'companiesStorePaymentMethods/readStorePaymentMethods',
      resetStorePaymentMethods: 'companiesStorePaymentMethods/resetData',
      readStorePaymentMethodsPreview: 'companiesStorePaymentMethods/readStorePaymentMethodsPreview',
      readSites: 'companiesSites/readSites',
      updateSites: 'companiesSites/updateSites',
      resetSites: 'companiesSites/resetSites',
      readStoreGmb: 'companiesStoreGmb/readStoreGmb',
      createStoreGmb: 'companiesStoreGmb/createStoreGmb',
      updateStoreGmb: 'companiesStoreGmb/updateStoreGmb',
      changeSyncedAll: 'companiesStoreGmb/changeSyncedAll',
      resetLocation: 'companiesStoreGmb/resetLocation',
      readBusinessGroups: 'companiesGmbAccounts/readCompaniesGmbAccounts',
      readStoreGmbLocations: 'companiesStoreGmbLocations/readStoreGmbLocations',
      readCompany: 'companiesCompany/readCompany',
      readExternalAccounts: 'companiesStoreExternalAccounts/readExternalAccounts',
      deleteExternalAccount: 'companiesStoreExternalAccounts/deleteExternalAccount',
      readAreas: 'companiesAreas/readAreas',
      readCompetitors: 'companiesStoreCompetitors/readCompetitors',
      deleteCompetitor: 'companiesStoreCompetitors/deleteCompetitor',
      readGmbOpenInfo: 'companiesStoreGmbOpenInfo/readGmbOpenInfo',
      readOriginalTagGroups: 'companiesOriginalTagGroups/readOriginalTagGroups',
      readStoreFoodMenu: 'companiesStoreFoodMenu/readStoreFoodMenu',
      readFoodMenus: 'companiesFoodMenus/readFoodMenus',
      readFoodMenu: 'companiesFoodMenu/readFoodMenu',
      updateStoreFoodMenu:'companiesStoreFoodMenu/updateStoreFoodMenu',
      readMediaLibraries: 'companiesStoreMediaLibraries/readMediaLibraries',
      readStoreGmbCategories: 'companiesStoreGmbCategories/readGmbCategories',
      readGmbAttributes: 'companiesStoreGmbAttributes/readGmbAttributes',
      readGmbFoodorderings: 'companiesStoreGmbFoodorderings/readGmbFoodorderings',
      readStoreOcShufoo: 'companiesStoreOcShufoo/readStoreOcShufoo',
      createStoreOcShufoo: 'companiesStoreOcShufoo/createStoreOcShufoo',
      deleteStoreOcShufoo: 'companiesStoreOcShufoo/deleteStoreOcShufoo',
      readStoreOcShufooAttributes: 'companiesStoreOcShufooAttributes/readStoreOcShufooAttributes',
      readStoreOcMapionBiz: 'companiesStoreOcMapionBiz/readStoreOcMapionBiz',
      createStoreOcMapionBiz: 'companiesStoreOcMapionBiz/createStoreOcMapionBiz',
      deleteStoreOcMapionBiz: 'companiesStoreOcMapionBiz/deleteStoreOcMapionBiz',
      readStoreOcMapionBizAttributes: 'companiesStoreOcMapionBizAttributes/readStoreOcMapionBizAttributes',
      readStoreTabelogAttributes: 'companiesStoreTabelogAttributes/readStoreTabelogAttributes',
      initStoreServicePatterns: 'companiesStoreServicePatterns/initData',
      readStoreServicePatterns: 'companiesStoreServicePatterns/readStoreServicePatterns',
      initCategoriesWithServicePatterns: 'companiesStoreGmbCategoriesWithServicePatterns/initData',
      readCategoriesWithServicePatterns: 'companiesStoreGmbCategoriesWithServicePatterns/readCategoriesWithServicePatterns',
      updateStoreServicePattern:'companiesStoreServicePatterns/updateStoreServicePattern',
      readStoreGmbMetadata:'companiesStoreGmbMetadata/readStoreGmbMetadata',
      setFranchiseeId: 'companiesStoreOption/setFranchiseeId',
      setUserCompanyId: 'companiesStoreOption/setUserCompanyId',
      setAreaId: 'companiesStoreOption/setAreaId',
      setOriginalTagGroupTagId: 'companiesStoreOption/setOriginalTagGroupTagId',
      setFranchiseeVal: 'companiesStoreOption/setFranchiseeVal',
      setUserCompanyVal: 'companiesStoreOption/setUserCompanyVal',
      setAreaVal: 'companiesStoreOption/setAreaVal',
      setOriginalTagGroupTagVal: 'companiesStoreOption/setOriginalTagGroupTagVal',
    }),
    ...mapMutations({
      initNewFoodMenu: 'companiesFoodMenu/initNewFoodMenu',
    }),
    initData() {
      this.initNewFoodMenu()
      this.initStoreServicePatterns()
      this.initCategoriesWithServicePatterns()
    },
    /**
     * @param { groupData } groupData
     * @param { string | boolean } value
     */
    extractDisplayName(groupData, value) {
      return groupData.valueMetadata.find(v => v.value === value).displayName
    },
    /**
     * @param { groupData[] } groupDataList
     */
    isGroupDataSet(groupDataList) {
      let bool = false
      groupDataList.forEach(data => {
        switch (data.valueType) {
        case gmbLocationAttributeValueTypes[2]:
          if (data.repeatedEnumValue) {
            bool = true
          }
          break
        default:
          if (data.values) {
            bool = true
          }
        }
      })
      return bool
    },
    async fetchStoreInfo() {
      if(this.providedFeaturesStoreOption) this.readStoreOption({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
      if(this.providedFeaturesPaymentMethod) this.readStorePaymentMethods({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
      this.readStoreBusinessHours({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
      if (this.providedFeaturesBusinessHoursCrawler) this.readStoreBusinessHoursCrawlers({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })

      if(this.providedFeaturesGmb) {
        this.readStoreGmb({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
        this.readStoreGmbMetadata(this.storeUuid).then(() => {
          if (this.isServicePatternAvailable) {
            this.readStoreServicePatterns(this.storeUuid)
            this.readCategoriesWithServicePatterns(this.storeUuid)
          }
        })
      }

      if (this.isFoodMenuAvailable) {
        this.initNewFoodMenu()
        this.readStoreFoodMenu(this.storeUuid)
        this.readFoodMenus()
      }
      const actions = [this.readStore({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })]
      if(this.providedFeaturesStoreSocial) actions.push(this.readStoreSocial())
      await Promise.all(actions)
      if(this.providedFeaturesStoreSocial) this.initialSocialUrl()
      this.title = this.$t('pages.CompaniesLocationStore.title', { name: this.store.attributes.name })
    },
    clickMenu(key) {
      if (key === 'connection') {
        this.$router.push({ name: 'LocationStoreConnects'})
      } else {
        this.selectedMenu = key
      }
    },
    async handleSitesSubmit() {
      await this.updateSites({ company_uuid: this.companyUuid, store_uuid: this.storeUuid, sites: this.filtered_sites })
    },
    async handleStoreOptionSubmit() {
      const result = await this.$refs.storeOptionForm.validate()
      if (!result.valid) {
        alert(this.$t('messages.invalid'))
        return
      }

      this.updateStoreOption({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    },
    async handleStoreSocialSubmit() {
      const payload = {
        store_social: {
          facebook_url: this.facebookUrl,
          instagram_url: this.instagramUrl,
          line_url: this.lineUrl,
          twitter_url: this.twitterUrl
        },
      }
      this.updateStoreSocial(payload)
    },
    handleChangeSyncedAll(e) {
      this.changeSyncedAll(e.target.checked)
    },
    handleGmbStoreSubmit() {
      this.showConfirmationModal = false
      this.store_gmb.attributes.location = this.store_gmb.attributes.location.id
      const payload = {
        store_gmb: {
          location: this.store_gmb.attributes.location
        },
      }
      this.createStoreGmb({ company_uuid: this.companyUuid, store_uuid: this.storeUuid, payload: payload }).then((res) => {
        if (!res?.errors){
          this.fetchStoreInfo()
        }
      })
    },
    handleUpdateGmbLocationSubmit() {
      this.updateStoreGmb({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    },
    async handleDeleteCompetitorSubmit(competitor_id) {
      const isSuccess = await this.deleteCompetitor({ company_uuid: this.currentCompanyUuid, store_uuid: this.storeUuid, competitor_id: competitor_id })
      if(isSuccess) this.readStore({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    },
    async handleShufooConnect() {
      const isSuccess = await this.createStoreOcShufoo(this.shufooId)
      if(isSuccess) {
        this.fetchStoreInfo()
        this.readStoreOcShufooAttributes()
      }
    },
    async onDeleteShufooConnect(){
      if (!confirm(this.$t('pages.CompaniesLocationStore.connectionWithShufoo.disconnectedConfirm'))) return
      const isSuccess = await this.deleteStoreOcShufoo()
      if(isSuccess) {
        this.displaySiteSetting = 'google'
        this.fetchStoreInfo()
      }
    },
    async handleMapionBizConnect() {
      const isSuccess = await this.createStoreOcMapionBiz(this.mapionBizId)
      if(isSuccess) {
        this.fetchStoreInfo()
        this.readStoreOcMapionBizAttributes()
      }
    },
    async onDeleteMapionBizConnect(){
      if (!confirm(this.$t('pages.CompaniesLocationStore.connectionWithMapionBiz.disconnectedConfirm'))) return
      const isSuccess = await this.deleteStoreOcMapionBiz()
      if(isSuccess) {
        this.displaySiteSetting = 'google'
        this.fetchStoreInfo()
      }
    },
    changeFoodMenu(menuId){
      this.readFoodMenu(menuId)
    },
    handleUpdateStoreFoodMenu(menuId){
      this.updateStoreFoodMenu({ store_uuid: this.storeUuid, id: menuId })
    },
    async handleUpdateStoreServicePattern(servicePatternId){
      await this.updateStoreServicePattern({ storeUuid: this.storeUuid, servicePatternId: servicePatternId })
      this.readStoreServicePatterns(this.storeUuid)
    },
    changeBusinessGroup(accountId) {
      if(!accountId) return

      this.selectedBusinessGroupId = accountId
      // 選択されていたlocationがあればreset
      this.resetLocation()

      const payload = {
        companyUuid: this.currentCompanyUuid,
        accountId: this.selectedBusinessGroupId,
      }
      this.readStoreGmbLocations(payload)
    },
    reloadSites() {
      this.resetSites()
    },
    formatAverage(ave) {
      return parseFloat(ave).toFixed(1)
    },
    regularHour(weekday) {
      return this.displayRegularHours.find(businessHour => businessHour.wday === weekday)
    },
    displayHour,
    displayMoreHoursTypeName(gmbHoursTypeId) {
      const moreHoursType = this.storeBusinessHoursMeta.moreHoursTypes.find(v => v.gmbHoursTypeId === gmbHoursTypeId)
      return moreHoursType ? moreHoursType.localizedDisplayName : gmbHoursTypeId
    },
    displayWeekday(weekday) {
      return this.$t('labels.weekday.' + weekday)
    },
    toggleConfirmationModal() {
      this.showConfirmationModal = !this.showConfirmationModal
    },
    selectFranchiseeSuggest({suggest}) {
      this.setFranchiseeId(suggest.id)
      this.setFranchiseeVal(suggest.franchiseeName)
    },
    selectUserCompanySuggest({suggest}) {
      this.setUserCompanyId(suggest.id)
      this.setUserCompanyVal(suggest.userCompanyName)
    },
    selectAreaSuggest({suggest}) {
      this.setAreaId(suggest.id)
      this.setAreaVal(suggest.areaName)
    },
    selectOriginalTagSuggest({suggest}) {
      this.setOriginalTagGroupTagId({ tagId: suggest.id, kind: suggest.kind })
      this.setOriginalTagGroupTagVal({ kind: suggest.kind, val: suggest.tagName })
    },
    onClickDeleteExternalAccount(external_account_id) {
      if (confirm(this.$t('messages.confirmToDelete'))) {
        this.deleteExternalAccount({ company_uuid: this.currentCompanyUuid, store_uuid: this.storeUuid, external_account_id: external_account_id })
        this.displaySiteSetting = 'google'
      }
    },
    handleSuggestFieldClose(_, formField) {
      if (this.getFormVal(formField) === '') {
        this.handleInputVal({ value: this.cacheStoreValues[formField] }, formField)
      }
      this.cacheStoreValues[formField] = ''
    },
    handleSuggestFieldFocus(selectedValue, formField) {
      this.cacheStoreValues[formField] = selectedValue
      this.handleInputVal({ value: '' }, formField)
    },
    handleInputValForm({ value }, formField) {
      this.handleInputVal({ value }, formField)
    },
    getFormVal(input_val_name) {
      if (input_val_name === 'inputArea') { return this.inputValArea }
      if (input_val_name === 'inputAreaManager') { return this.inputValAreaManager }
      if (input_val_name === 'inputFranchisee') { return this.inputValFranchisee }

      //OriginalTagGroupTag
      if (['a', 'b', 'c', 'd', 'e'].includes(input_val_name)) { return this.inputValOriginalTag[input_val_name] }
    },
    handleInputVal({ value }, input_name) {
      if (input_name === 'inputArea') { this.setAreaVal(value) }
      if (input_name === 'inputAreaManager') { this.setUserCompanyVal(value) }
      if (input_name === 'inputFranchisee') { this.setFranchiseeVal(value) }

      //OriginalTagGroupTag
      if (['a', 'b', 'c', 'd', 'e'].includes(input_name)) { return this.setOriginalTagGroupTagVal({ kind: input_name, val: value }) }
    },
    initialSocialUrl() {
      this.facebookUrl = this.storeSocial.facebookUrl
      this.twitterUrl = this.storeSocial.twitterUrl
      this.instagramUrl = this.storeSocial.instagramUrl
      this.lineUrl = this.storeSocial.lineUrl
    },
    businessHourList() {
      return {
        regularHours: this.storeRegularHours,
        holidayRegularHour: this.storeHolidayRegularHour,
        dayBeforeHolidayRegularHour: this.storeDayBeforeHolidayRegularHour,
        wdayIndexRegularHours: this.storeWdayIndexRegularHours,
        regularHourAdditionalDescription: this.storeRegularHourAdditionalDescription,
        regularHolidayAdditionalDescription: this.storeRegularHolidayAdditionalDescription,
        specialHours:  this.storeSpecialHours,
        moreHours: this.storeMoreHours,
        synced: this.gmbLocationSynced.business_hours_synced,
      }
    },
    handleShowPaymentMethodsPreview() {
      this.readStorePaymentMethodsPreview({ companyUuid: this.currentCompanyUuid, storeUuid: this.storeUuid })
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../stylesheets/biz_spa/foundation/_var.scss";
.p-store-times {
  &__items {
    &:not(:first-child) {
      margin-top: 14px;
    }
  }
  &__item {
    display: flex;
    &:not(:first-child) {
      border-top: 1px solid #E4E7EB;
    }
  }
  &__title {
    font-weight: bold;
  }
  &__content {
    margin-left: 20px;
  }
}
.storeInfo-content-extended {
  flex-basis: 100%;
}
.p-formGrid--border-extended {
  padding: 12px;
}
.p-storeInfo-footer {
  margin-top: 24px;
}
.p-storeInfo-description {
  margin-top: 24px;
  border-top: 1px solid #E4E7EB;
  &__label {
    margin-top: 6px;
    font-weight: bold;
  }
  &__content {
    min-width: 0;
    margin-top: 4px;
  }
}
.openInfoStatusCaution {
  margin-top: 0 !important;
  margin-bottom: 16px;
  font-weight: bold;
  &__text {
    > a {
      color: #FFF;
    }
  }
}
.siteLists {
  display: flex;
  flex-wrap: wrap;
  margin-top: 4px;
  gap: 16px;
}
.p-formGrid {
  overflow: visible;
}
.siteFilter {
  width: 200px;
  margin-bottom: 16px;
}
.info {
  margin-top: 16px;
}
.site {
  display: flex;
  align-items: center;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
  letter-spacing: 1px;
  &__name {
    margin-left: 8px;
  }
}
.settings {
  &__item {
    display: flex;
    justify-content: space-between;
    padding: 0 32px;
    border: 1px solid #E4E7EB;
    border-radius: 4px;
    &:not(:first-child) {
      margin-top: 16px;
    }
    &--attributes {
      display: block;
    }
  }
  &__info {
    flex: 0 0 calc(100% - 128px);
    padding: 24px 0;
    &--attributes {
      padding-bottom: 0;
    }
  }
  &__name {
    margin-bottom: 16px;
    font-weight: bold;
    font-size: 12px;
    line-height: 1;
  }
  &__content {
    font-size: 14px;
    line-height: 1.5;
    word-break: break-all;
    svg {
      margin: 8px;
      vertical-align: middle;
    }
    .c-info__icon {
      svg {
        margin: 0;
        vertical-align: inherit;
      }
    }
  }
  .c-button {
    align-self: center;
    margin-top: 0;
    padding: 32px 0;
    &__item {
      margin: 0;
    }
  }
}
.siteSettingSection {
  .c-info,
  .site {
    &:not(:last-child) {
      margin-bottom: 28px;
    }
  }
  &__select {
    width: 30%;
    margin-bottom: 20px;
  }
}
.attributeGroup {
  margin: 24px 0;
  border-bottom: 1px solid #E4E7EB;
  &__groupDisplayName {
    font-weight: bold;
  }
  &__attributes {
    margin: 16px 0;
    line-height: 24px;
  }
}
.statusMark {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  &__open {
    background: $complete-color;
  }
  &__closedTemporarily {
    background: $color-accent-primary;
  }
  &__closedPermanently {
    background: #D2D5DB;
  }
}
.specialHours {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #E4E7EB;
  &__modalLink {
    color: $color-accent-primary;
    text-decoration: underline;
    cursor: pointer;
  }
}
.moreHour {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #E4E7EB;
}
.specialHours__title {
  margin-bottom: 14px;
  font-weight: bold;
  font-size: 14px;
  line-height: 1.5;
}
.moreHour__title {
  margin-bottom: 14px;
  font-weight: bold;
  font-size: 14px;
  line-height: 1.5;
}
.holidayIndexHour {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #E4E7EB;
}
.previewBusinessHourLink {
  color: $color-accent-primary;
  text-decoration: underline;
  cursor: pointer;
}
.previewWebsiteLink {
  margin-left: 20px;
  color: $color-accent-primary;
  text-decoration: underline;
  white-space: nowrap;
  cursor: pointer;
}
.shufooConnect {
  display: flex;
  justify-content: space-between;
}
.onlyDistributorOrAdmin {
  margin-left: 15px;
}
.icon {
  width: 16px;
  height: 16px;
}
.sampleUrls {
  word-wrap: break-word;
}
.webSiteUrl {
  display: block;
  word-break: break-all;
}
</style>
